import { LumberProfileType, PlywoodProfile, SpanRating } from "building-types/framing/lumberTypes";
import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import type { FacesWithMaterial } from "building-types/geometry/facesWithMaterial";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type { EntityDescriptor } from "building-types/views/viewEntityTypes";
import type {
  StructuralMaterialTable,
  StructuralMaterialBase,
  WallMaterial,
  FloorMaterial,
} from "building-types/walls/wallMaterialTypes";
import { WallMaterialType, FloorMaterialType } from "building-types/walls/wallMaterialTypesEnum";
import type { Face } from "geom/brep";
import type { UVTransformation } from "geom/uv";
import { PlywoodProfileCatalog16, PlywoodProfileCatalog24 } from "./lumberTools";

export const DEFAULT_EXTERIOR_FINISH_THICKNESS = 3 / 4;
export const DEFAULT_INTERIOR_FINISH_THICKNESS = 1 / 2;

const defaultSubstrateHalf = structuralMaterialNew(WallMaterialType.SUBSTRATE, `Substrate 1/2"`, 1 / 2);
const defaultSubstrateOne = structuralMaterialNew(WallMaterialType.SUBSTRATE, `Substrate 1"`, 1);
const defaultThermal = structuralMaterialNew(WallMaterialType.THERMAL, `Thermal`, 0);
const defaultMembrane = structuralMaterialNew(WallMaterialType.MEMBRANE, `Membrane`, 0);
const defaultStructuralMaterial2X4 = structuralWallMaterialNew(LumberProfileType.SPF_2X4);
const defaultStructuralMaterial2X6 = structuralWallMaterialNew(LumberProfileType.SPF_2X6);
const defaultStructuralMaterial2X8 = structuralWallMaterialNew(LumberProfileType.SPF_2X8);
const defaultStructuralMaterial2X10 = structuralWallMaterialNew(LumberProfileType.SPF_2X10);
const defaultStructuralMaterial2X12 = structuralWallMaterialNew(LumberProfileType.SPF_2X12);
const defaultPlywoodMaterialU1Over2SR16 = plywoodMaterialNew(PlywoodProfile["1/2-u"], SpanRating["SR16"]);
const defaultPlywoodMaterialU3Over4SR16 = plywoodMaterialNew(PlywoodProfile["3/4-u"], SpanRating["SR16"]);
const defaultPlywoodMaterialU19Over32SR16 = plywoodMaterialNew(PlywoodProfile["19/32-u"], SpanRating["SR16"]);
const defaultPlywoodMaterialU5Over8SR16 = plywoodMaterialNew(PlywoodProfile["5/8-u"], SpanRating["SR16"]);
const defaultPlywoodMaterialS1Over2SR24 = plywoodMaterialNew(PlywoodProfile["1/2-s"], SpanRating["SR24"]);
const defaultPlywoodMaterialS3Over4SR24 = plywoodMaterialNew(PlywoodProfile["3/4-s"], SpanRating["SR24"]);
const defaultPlywoodMaterialS19Over32SR24 = plywoodMaterialNew(PlywoodProfile["19/32-s"], SpanRating["SR24"]);
const defaultPlywoodMaterialS3Over8SR24 = plywoodMaterialNew(PlywoodProfile["3/8-s"], SpanRating["SR24"]);

export const defaultWallMaterials: StructuralMaterialTable = {
  ["defaultSubstrateHalf"]: defaultSubstrateHalf,
  ["defaultSubstrateOne"]: defaultSubstrateOne,
  ["defaultThermal"]: defaultThermal,
  ["defaultMembrane"]: defaultMembrane,
  ["defaultStructuralMaterial2X4"]: defaultStructuralMaterial2X4,
  ["defaultStructuralMaterial2X6"]: defaultStructuralMaterial2X6,
  ["defaultStructuralMaterial2X8"]: defaultStructuralMaterial2X8,
  ["defaultStructuralMaterial2X10"]: defaultStructuralMaterial2X10,
  ["defaultStructuralMaterial2X12"]: defaultStructuralMaterial2X12,
  ["defaultPlywoodMaterialu1over2sr16"]: defaultPlywoodMaterialU1Over2SR16,
  ["defaultPlywoodMaterialU3Over4SR16"]: defaultPlywoodMaterialU3Over4SR16,
  ["defaultPlywoodMaterialU19Over32SR16"]: defaultPlywoodMaterialU19Over32SR16,
  ["defaultPlywoodMaterialU5Over8SR16"]: defaultPlywoodMaterialU5Over8SR16,
  ["defaultPlywoodMaterialS1Over2SR24"]: defaultPlywoodMaterialS1Over2SR24,
  ["defaultPlywoodMaterialS3Over4SR24"]: defaultPlywoodMaterialS3Over4SR24,
  ["defaultPlywoodMaterialS19Over32SR24"]: defaultPlywoodMaterialS19Over32SR24,
  ["defaultPlywoodMaterialS3Over8SR24"]: defaultPlywoodMaterialS3Over8SR24,
};

export const wallMaterialIdFromLumberProfile: {
  [key in Exclude<LumberProfileType, LumberProfileType.CUSTOM>]: string;
} = {
  [LumberProfileType.SPF_2X4]: "defaultStructuralMaterial2X4",
  [LumberProfileType.SPF_2X6]: "defaultStructuralMaterial2X6",
  [LumberProfileType.SPF_2X8]: "defaultStructuralMaterial2X8",
  [LumberProfileType.SPF_2X10]: "defaultStructuralMaterial2X10",
  [LumberProfileType.SPF_2X12]: "defaultStructuralMaterial2X12",
};

export const substrateMaterialIdFromPlywoodProfileSR16: {
  [key in PlywoodProfile]?: string;
} = {
  [PlywoodProfile["1/2-u"]]: "defaultPlywoodMaterialU1Over2SR16",
  [PlywoodProfile["3/4-u"]]: "defaultPlywoodMaterialU3Over4SR16",
  [PlywoodProfile["19/32-u"]]: "defaultPlywoodMaterialU19Over32SR16",
  [PlywoodProfile["5/8-u"]]: "defaultPlywoodMaterialU5Over8SR16",
};

export const substrateMaterialIdFromPlywoodProfileSR24: {
  [key in PlywoodProfile]?: string;
} = {
  [PlywoodProfile["1/2-s"]]: "defaultPlywoodMaterialS1Over2SR24",
  [PlywoodProfile["3/4-s"]]: "defaultPlywoodMaterialS3Over4SR24",
  [PlywoodProfile["19/32-s"]]: "defaultPlywoodMaterialS19Over32SR24",
  [PlywoodProfile["3/8-s"]]: "defaultPlywoodMaterialS3Over8SR24",
};

export const defaultWallMaterialIdByType: {
  [key in WallMaterialType]?: string;
} = {
  [WallMaterialType.STRUCTURE]: "defaultStructuralMaterial2X4",
  [WallMaterialType.SUBSTRATE]: "defaultSubstrateHalf",
  [WallMaterialType.THERMAL]: "defaultThermal",
  [WallMaterialType.MEMBRANE]: "defaultMembrane",
};

export const defaultWallMaterialIds = new Set([
  ...Object.keys(defaultWallMaterials),
  ...Object.values(defaultWallMaterialIdByType),
  ...Object.values(wallMaterialIdFromLumberProfile),
  ...Object.values(substrateMaterialIdFromPlywoodProfileSR16),
  ...Object.values(substrateMaterialIdFromPlywoodProfileSR24),
]);

export function structuralMaterialNew<T>(type: T, name: string, thickness: number): StructuralMaterialBase<T> {
  return {
    catalogItemType: "STRUCTURAL_MATERIAL",
    type,
    name,
    thickness,
  };
}
function structuralWallMaterialNew(lumberProfileType: LumberProfileType): WallMaterial {
  const { name, width } = LumberProfileCatalog[lumberProfileType];
  return structuralMaterialNew(WallMaterialType.STRUCTURE, name, width);
}

function plywoodMaterialNew(plywoodType: PlywoodProfile, sr: SpanRating): FloorMaterial {
  if (sr === SpanRating.SR16) {
    const { name, thickness } = PlywoodProfileCatalog16[plywoodType];
    return structuralMaterialNew(FloorMaterialType.SUBSTRATE_PLYWOOD_SR16, name, thickness);
  } else if (sr === SpanRating.SR24) {
    const { name, thickness } = PlywoodProfileCatalog24[plywoodType];
    return structuralMaterialNew(FloorMaterialType.SUBSTRATE_PLYWOOD_SR24, name, thickness);
  } else {
    return structuralMaterialNew(FloorMaterialType.SUBSTRATE_PLYWOOD_SR16, "PLYWOOD", 0.75);
  }
}

export function facesWithMaterialNew(
  faces: Face[],
  material: PaletteMaterialSlot,
  entityHandleForSelectingReportingUnit?: DocEntityHandle,
  materialTransformation?: UVTransformation,
  descriptor?: EntityDescriptor,
  entityId?: string,
  pickHandle?: DocEntityHandle,
): FacesWithMaterial {
  return {
    entityId,
    descriptor,
    faces,
    material,
    pickHandle,
    entityHandleForSelectingReportingUnit,
    materialTransformation,
  };
}

/**
 * @TODO - should be assigned on the material
 */
export const isHatchMaterialPaletteSlot: { [K in PaletteMaterialSlot]?: true } = {
  masonry0: true,
  masonry1: true,
  masonry2: true,
  masonry3: true,
  "masonryTrim0:Base": true,
  "masonryTrim1:Base": true,
  "masonryTrim2:Base": true,
  "masonryTrim3:Base": true,
  "masonryTrim0:Header": true,
  "masonryTrim1:Header": true,
  "masonryTrim2:Header": true,
  "masonryTrim3:Header": true,
  "masonryTrim0:Rowlock": true,
  "masonryTrim1:Rowlock": true,
  "masonryTrim2:Rowlock": true,
  "masonryTrim3:Rowlock": true,
};
